import Cookies from 'js-cookie';
import { isDeviceCookie } from '../clearCognitoCookies';

interface CookieStorageConfig {
  domain: string;
  path?: string;
  expires?: number;
  secure?: boolean;
  sameSite?: string;
}

/** @class */
export default class CookieStorage {
  domain: any;
  path: any;
  expires: any;
  secure: any;
  sameSite: any;
  /**
   * Constructs a new CookieStorage object
   * @param {object} data Creation options.
   * @param {string} data.domain Cookies domain (mandatory).
   * @param {string} data.path Cookies path (default: '/')
   * @param {integer} data.expires Cookie expiration (in days, default: 365)
   * @param {boolean} data.secure Cookie secure flag (default: true)
   * @param {string} data.sameSite Cookie request behaviour (default: null)
   */
  constructor(data: CookieStorageConfig) {
    if (data.domain) {
      this.domain = data.domain;
    } else {
      throw new Error('The domain of cookieStorage can not be undefined.');
    }
    if (data.path) {
      this.path = data.path;
    } else {
      this.path = '/';
    }
    if (Object.prototype.hasOwnProperty.call(data, 'expires')) {
      this.expires = data.expires;
    } else {
      this.expires = 365;
    }
    if (Object.prototype.hasOwnProperty.call(data, 'secure')) {
      this.secure = data.secure;
    } else {
      this.secure = true;
    }
    if (Object.prototype.hasOwnProperty.call(data, 'sameSite')) {
      if (data.sameSite && !['strict', 'lax', 'none'].includes(data.sameSite)) {
        throw new Error('The sameSite value of cookieStorage must be "lax", "strict" or "none".');
      }
      if (data.sameSite === 'none' && !this.secure) {
        throw new Error('sameSite = None requires the Secure attribute in latest browser versions.');
      }
      this.sameSite = data.sameSite;
    } else {
      this.sameSite = null;
    }
  }

  /**
   * This is used to set a specific item in storage
   * @param {string} key - the key for the item
   * @param {object} value - the value
   * @returns {string} value that was set
   */
  setItem(key: string, value: string, _expires?: number): string | null {
    const deviceCookie = isDeviceCookie(key);

    const options = {
      path: this.path,
      domain: this.domain,
      secure: this.secure,
      ...(this.sameSite ? { sameSite: this.sameSite } : {}),
      ...(deviceCookie ? { expires: 30 } : {}),
    };

    Cookies.set(key, value, options);
    return Cookies.get(key) || null;
  }

  /**
   * This is used to get a specific key from storage
   * @param {string} key - the key for the item
   * @returns {string} value that was set
   */
  getItem(key: string): string | null {
    const cookieValue = Cookies.get(key);
    return cookieValue || null;
  }

  /**
   * This is used to remove an item from storage
   * @param {string} key - the key being set
   * @returns {string} value - value that was deleted
   */
  removeItem(key: string, expires?: number): void {
    const options = {
      path: this.path,
      expires: expires ?? this.expires,
      domain: this.domain,
      secure: this.secure,
      ...(this.sameSite ? { sameSite: this.sameSite } : {}),
    };

    Cookies.remove(key, options);
  }

  /**
   * This is used to clear the storage
   * @returns {object} nothing
   */
  clear(): void {
    const cookies = Cookies.get();
    const cookieKeys = Object.keys(cookies);
    for (let index = 0; index < cookieKeys.length; ++index) {
      Cookies.remove(cookieKeys[index]);
    }
  }

  setDomain(domain: string): void {
    this.domain = domain;
  }
}
